import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { getTotalUnreadNotifications } from "src/services/NotificationAnnouncementsAPIs";
import NotificationService from "src/services/NotificationService";

interface NotificationContextProps {
  count: number;
  refreshCount: () => Promise<void>;
  /** Aligns badge with unread count derived from `fetchAllNotifications` (same source as the header popup). */
  syncUnreadWithFetchedList: (_unreadTotal: number) => void;
  pushEnabled: boolean;
  pushPermission: NotificationPermission;
  enablePushNotifications: (_userId: number) => Promise<void>;
  disablePushNotifications: (_userId: number) => Promise<void>;
  requestPushPermission: () => Promise<NotificationPermission>;
}

const noop = () => undefined;
const asyncNoop = async () => undefined;

const NotificationContext = createContext<NotificationContextProps>({
  count: 0,
  refreshCount: asyncNoop,
  syncUnreadWithFetchedList: noop,
  pushEnabled: false,
  pushPermission: "default",
  enablePushNotifications: asyncNoop,
  disablePushNotifications: asyncNoop,
  requestPushPermission: async () => "default",
});

const PUSH_ENABLED_KEY = "push_notifications_enabled";
const UNREAD_COUNT_KEY = "total_unread_count";

const persistPushEnabled = (enabled: boolean) => {
  localStorage.setItem(PUSH_ENABLED_KEY, String(enabled));
};

const normalizeUnreadCount = (value: unknown): number => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.min(Math.floor(value), 1_000_000);
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Do not hydrate from localStorage: a stale total produced a misleading badge (e.g. "99+")
  // while the notifications list from the API was empty. Count comes from refreshCount / list sync.
  const [count, setCount] = useState<number>(0);
  const [pushEnabled, setPushEnabled] = useState<boolean>(
    () => localStorage.getItem(PUSH_ENABLED_KEY) === "true"
  );
  const [pushPermission, setPushPermission] = useState<NotificationPermission>("default");
  /** Bumps when `syncUnreadWithFetchedList` runs so in-flight `refreshCount` does not overwrite list-backed totals. */
  const countMutationGenerationRef = useRef(0);

  const syncUnreadWithFetchedList = useCallback((unreadTotal: number) => {
    countMutationGenerationRef.current += 1;
    const safe = normalizeUnreadCount(unreadTotal);
    setCount(safe);
    localStorage.setItem(UNREAD_COUNT_KEY, String(safe));
  }, []);

  const refreshCount = useCallback(async () => {
    const generationAtStart = countMutationGenerationRef.current;
    try {
      const latestCount = await getTotalUnreadNotifications();
      if (countMutationGenerationRef.current !== generationAtStart) {
        return;
      }
      const safe = normalizeUnreadCount(latestCount);
      setCount(safe);
      localStorage.setItem(UNREAD_COUNT_KEY, String(safe));
    } catch (error) {
      console.error("Failed to refresh unread count", error);
      if (countMutationGenerationRef.current !== generationAtStart) {
        return;
      }
      setCount(0);
      localStorage.removeItem(UNREAD_COUNT_KEY);
    }
  }, []);

  const requestPushPermission = async () => {
    const permission = await NotificationService.requestPermission();
    setPushPermission(permission);
    return permission;
  };

  const enablePushNotifications = async (_userId?: number) => {
    try {
      const permission = await requestPushPermission();
      if (permission === "granted") {
        await NotificationService.registerServiceWorker();
        const subscription = await NotificationService.subscribeUserToPush();
        if (subscription) {
          setPushEnabled(true);
          persistPushEnabled(true);
        }
      }
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      setPushEnabled(false);
      persistPushEnabled(false);
    }
  };

  const disablePushNotifications = async (_userId?: number) => {
    try {
      const success = await NotificationService.unsubscribeFromPush();
      if (success) {
        setPushEnabled(false);
        persistPushEnabled(false);
      }
    } catch (error) {
      console.error("Error disabling push notifications:", error);
    }
  };

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        await NotificationService.registerServiceWorker();

        const permission = await NotificationService.checkPermission();
        setPushPermission(permission);

        // If push was previously enabled and permission is granted, resubscribe
        if (pushEnabled && permission === "granted") {
          const subscription = await NotificationService.subscribeUserToPush();
          setPushEnabled(!!subscription);
        }

        // Refresh notification count
        await refreshCount();
      } catch (error) {
        console.error("Error initializing notifications:", error);
        setPushEnabled(false);
        persistPushEnabled(false);
      }
    };

    initializeNotifications();
  }, [refreshCount]);

  return (
    <NotificationContext.Provider
      value={{
        count,
        refreshCount,
        syncUnreadWithFetchedList,
        pushEnabled,
        pushPermission,
        enablePushNotifications,
        disablePushNotifications,
        requestPushPermission
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
